<template>
  <a-modal
    v-model:visible="visible"
    :centered="true"
    :width="454"
    :bodyStyle="{ padding: '0 24px 20px' }"
    :footer="null"
    :destroyOnClose="true"
    wrapClassName="revisit_confirm_modal"
    @cancel="handleCancel"
  >
    <template #title>
      <span class="modal_title">提示</span>
    </template>

    <!-- 多对医患关系 -->
    <div class="confirm_body" v-if="bussBar.showMultipleTips">
      <!-- 无法回访 -->
      <div class="category_block" v-if="cannotRevisitTotal > 0">
        <div class="category_title">无法回访（{{ cannotRevisitTotal }}个）</div>
        <!-- 工单进行中 -->
        <div class="tag_container cannot_revisit" v-if="localData.workOrderBlockedPatients.length">
          <div class="sub_section">
            <div class="sub_title">
              工单进行中（{{ localData.workOrderBlockedPatients.length }}个）
            </div>
            <div class="tag_row">
              <span
                v-for="item in localData.workOrderBlockedPatients"
                :key="item.ucPatientId"
                class="patient_tag"
              >
                {{ item.blockMessage }}
              </span>
            </div>
          </div>
        </div>
        <!-- 已在回访池 -->
        <div class="tag_container cannot_revisit" v-if="localData.inPoolBlockedPatients.length">
          <div class="sub_section">
            <div class="sub_title">
              已在回访池（{{ localData.inPoolBlockedPatients.length }}个）
            </div>
            <div class="tag_row">
              <span
                v-for="item in localData.inPoolBlockedPatients"
                :key="item.ucPatientId"
                class="patient_tag"
              >
                {{ item.blockMessage }}
              </span>
            </div>
          </div>
        </div>
        <!-- 今日已完成回访 -->
        <div
          class="tag_container cannot_revisit"
          v-if="localData.todayCompletedBlockedPatients.length"
        >
          <div class="sub_section">
            <div class="sub_title">
              今日已完成回访（{{ localData.todayCompletedBlockedPatients.length }}个）
            </div>
            <div class="tag_row">
              <span
                v-for="item in localData.todayCompletedBlockedPatients"
                :key="item.ucPatientId"
                class="patient_tag"
              >
                {{ item.blockMessage }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 可加入主动回访池 -->
      <div v-if="localData.availablePatients.length" class="category_block">
        <div class="category_title">
          可加入主动回访池（{{ localData.availablePatients.length }} 个）
        </div>
        <div class="warn" v-if="visitAction === 1 && overflowNum > 0">
          按回访任务{{ localData.quota.limit }}条数量限制目前已超标，请移除{{ overflowNum }}个
        </div>
        <div class="tag_container can_join">
          <span
            class="patient_tag"
            v-for="item in localData.availablePatients"
            :key="item.ucPatientId"
          >
            <span>{{ item.blockMessage }}</span>
            <a-popconfirm
              title="确定删除该人员吗？"
              ok-text="确定"
              cancel-text="取消"
              @confirm="removePatient('availablePatients', item.ucPatientId)"
            >
              <CloseOutlined class="tag_close" />
            </a-popconfirm>
          </span>
        </div>
      </div>

      <!-- 分配健管（仅分配回访模式） -->
      <div v-if="bussBar.canSelectManager" class="manager_block">
        <div class="manager_header">
          <div class="manager_title">
            分配健管
            <span class="required">*</span>
          </div>
          <div class="warn" v-if="selectedManager">
            目前该健管回访池未完成的任务：{{ selectedManagerObj?.unfinishedCount || 0 }}条
          </div>
        </div>

        <a-select
          v-model:value="selectedManager"
          placeholder="选择/搜索健管"
          style="width: 100%"
          show-search
          :options="managerOptions"
          :filter-option="filterOption"
          allowClear
          :dropdownStyle="{ height: '200px' }"
        />
      </div>
    </div>
    <!-- 一对医患关系 -->
    <div v-if="bussBar.singleTips" class="info_tip">
      {{ bussBar.singleTips }}
    </div>
    <!-- Footer -->
    <div class="confirm_footer">
      <template v-if="bussBar.footerShowMode === 1">
        <a-button class="cancel_btn" @click="handleCancel" :loading="loading">取 消</a-button>
        <a-button
          type="primary"
          :disabled="confirmDisabled"
          :loading="loading"
          @click="handleConfirm"
        >
          {{ visitAction === 1 ? '确定加入' : '确定分配' }}
        </a-button>
      </template>

      <a-button
        v-if="bussBar.footerShowMode === 2"
        type="primary"
        :loading="loading"
        @click="handleCancel"
      >
        知道了
      </a-button>
    </div>
  </a-modal>
</template>

<script>
export default { name: 'RevisitConfirm' }
</script>

<script setup>
import { ref, reactive, computed, defineExpose, defineEmits, defineProps } from 'vue'
import { CloseOutlined } from '@ant-design/icons-vue'
import {
  joinActiveFollowupPairs,
  getDoctorAssistantList,
  checkPatientsForActiveFollowup
} from '@/api/UserPool'
import { message } from 'ant-design-vue'

const props = defineProps({
  userId: {
    type: Number
  },
  ucDoctorId: {
    type: Number
  }
})

const emit = defineEmits(['confirm', 'cancel'])

const visible = ref(false)
// 1-主动回访 2-分配回访
const visitAction = ref(1)
const selectedManager = ref(undefined)
const loading = ref(false)

const managerOptions = ref([])

// 医患对映射：ucPatientId -> ucDoctorId（多对医患关系批量提交时使用）
const patientDoctorMap = ref(new Map())
const localData = reactive({
  quota: {}, //额度与确认按钮状态
  workOrderBlockedPatients: [], // 工单进行中、无法回访患者列表
  inPoolBlockedPatients: [], // 已在回访池
  todayCompletedBlockedPatients: [], // 今日已回访
  availablePatients: [] // 可加入主动访池患者列表
})

const cannotRevisitTotal = computed(
  () =>
    localData.workOrderBlockedPatients.length +
    localData.inPoolBlockedPatients.length +
    localData.todayCompletedBlockedPatients.length
)
const selectedManagerObj = computed(() =>
  managerOptions.value.find(item => item.ucUserId === selectedManager.value)
)

// 确认按钮 disabled 条件
const confirmDisabled = computed(() => {
  if (localData.availablePatients.length === 0) return true
  if (visitAction.value === 2 && !selectedManager.value) return true
  if (visitAction.value === 1) {
    if (localData.quota.remainSlots <= 0) {
      return true // 名额满了
    } else {
      return localData.availablePatients.length > localData.quota.remainSlots // 超过名额
    }
  }
  return false
})
//  动态计算还需要移除几个医患
const overflowNum = computed(() => {
  try {
    const remainSlots = localData?.quota?.remainSlots || 0
    const availableLen = localData?.availablePatients?.length || 0
    return availableLen - remainSlots
  } catch {
    return 0
  }
})
const filterOption = (input, option) => {
  return option.label.toLowerCase().includes(input.toLowerCase())
}

const fetchDoctorAssistantList = async () => {
  const res = await getDoctorAssistantList()

  managerOptions.value = (res || [])
    .map(item => ({
      label: item.realName,
      value: item.ucUserId,
      ...item
    }))
    .filter(item => item.value !== props.userId)
}

/**
 * 打开弹窗
 * @param {Object} options
 * @param {Number} options.visitAction - 1:主动回访 2:分配回访
 * @param {Array} options.ucPatientIds - 患者ID列表(废弃)
 * @param {Array} [options.patientDoctorPairs] - 医患对列表 [{ ucPatientId, ucDoctorId }]，传了则走多对医患关系提交
 */
async function initModal(options = {}) {
  visitAction.value = options.visitAction || 1
  selectedManager.value = undefined

  // 医患对映射（多对医患关系批量提交时使用）
  const pairs = options.patientDoctorPairs || []
  patientDoctorMap.value = new Map(pairs.map(item => [item.ucPatientId, item.ucDoctorId ?? 0]))

  const assignedBy = visitAction.value === 1 ? 0 : props.userId

  const res = await checkPatientsForActiveFollowup({
    items: pairs,
    assignedBy
  })

  const {
    quota,
    workOrderBlockedPatients,
    inPoolBlockedPatients,
    todayCompletedBlockedPatients,
    availablePatients
  } = res || {}

  localData.quota = quota || {}
  localData.workOrderBlockedPatients = [...(workOrderBlockedPatients || [])]
  localData.inPoolBlockedPatients = [...(inPoolBlockedPatients || [])]
  localData.todayCompletedBlockedPatients = [...(todayCompletedBlockedPatients || [])]
  localData.availablePatients = [...(availablePatients || [])]

  // if (
  //   availablePatients?.length === 0 &&
  //   workOrderBlockedPatients?.length === 0
  //   // && activeFollowupBlockedPatients?.length !== 0
  // ) {
  //   message.warning('已在回访池，无法加入')
  //   return
  // }
  handleButton()

  if (visitAction.value === 2) {
    fetchDoctorAssistantList()
  }

  visible.value = true
}
const defaultBussBar = {
  showMultipleTips: false, //多对医患关系的提示
  footerShowMode: undefined, //底部按钮显示: 1-确定, 2 知道了
  canSelectManager: false, //是否可以选择健管
  singleTips: '' //提示文案(单个医患组且无法加入回访池的时候出现的提示)
}
const bussBar = reactive({
  ...defaultBussBar
})
// 处理各种场景
function handleButton() {
  Object.assign(bussBar, defaultBussBar)
  const {
    quota,
    workOrderBlockedPatients,
    inPoolBlockedPatients,
    todayCompletedBlockedPatients,
    availablePatients
  } = localData
  const action = visitAction.value
  const isMultiple = patientDoctorMap.value.size > 1 // 是否多对医患关系
  const availableLen = availablePatients.length //能加入主动回访池的患者数

  if (action === 1 && quota?.remainSlots <= 0) {
    //名额满了
    bussBar.singleTips = `你的主动回访任务已满足${quota.limit}条，无法加入回访任务！`
    bussBar.footerShowMode = 2
    return
  }
  if (isMultiple) {
    //多个医患组
    bussBar.showMultipleTips = true
    bussBar.footerShowMode = availableLen ? 1 : 2
    bussBar.canSelectManager = availableLen && action === 2
  } else {
    // 单个医患组
    if (availableLen) {
      // 可加入回访池
      bussBar.showMultipleTips = true
      bussBar.footerShowMode = 1
      bussBar.canSelectManager = action === 2
    } else {
      // 无法加入回访池
      bussBar.footerShowMode = 2
      const suffix = visitAction.value === 1 ? '加入' : '分配'
      if (workOrderBlockedPatients.length > 0) {
        const item = workOrderBlockedPatients[0]
        bussBar.singleTips = `${item.blockMessage}已有工单进行中，无法${suffix}。`
      } else if (inPoolBlockedPatients.length > 0) {
        const item = inPoolBlockedPatients[0]
        bussBar.singleTips = `${item.blockMessage}已在回访池，无法${suffix}。`
      } else if (todayCompletedBlockedPatients.length > 0) {
        const item = todayCompletedBlockedPatients[0]
        bussBar.singleTips = `${item.blockMessage}今日已回访，无法${suffix}。`
      }
    }
  }
}
function removePatient(category, id) {
  localData[category] = localData[category].filter(item => item.ucPatientId !== id)
}

async function handleConfirm() {
  loading.value = true
  try {
    const assignedBy = visitAction.value === 1 ? 0 : props.userId
    const daUcUserId = visitAction.value === 1 ? props.userId : selectedManager.value

    await joinActiveFollowupPairs({
      items: localData.availablePatients.map(item => ({
        ucPatientId: item.ucPatientId,
        ucDoctorId: patientDoctorMap.value.get(item.ucPatientId) ?? 0
      })),
      assignedBy,
      daUcUserId
    })
    message.success('加入成功')
    emit('confirm')
    visible.value = false
  } finally {
    loading.value = false
  }
}

function handleCancel() {
  emit('cancel')
  visible.value = false
}

defineExpose({ initModal })
</script>

<style lang="scss">
// 非 scoped：modal teleport 到 body
.revisit_confirm_modal {
  .ant-modal-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.09);
    margin-bottom: 0;
    padding: 16px 24px;
  }
  .ant-modal-title {
    .modal_title {
      font-size: 16px;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.85);
      font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, sans-serif;
    }
  }
}
</style>

<style scoped lang="scss">
.confirm_body {
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

// 分类区块
.category_block {
  display: flex;
  flex-direction: column;
  gap: 14px;
  .warn {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #fc8a0f;
  }
}

// 分类标题：15px 500 #1A1A1A
.category_title {
  font-size: 15px;
  font-weight: 500;
  line-height: 21px;
  color: #1a1a1a;
  font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, sans-serif;
}

// 标签容器
.tag_container {
  border-radius: 4px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 200px;
  overflow-y: auto;

  // 可加入：浅蓝底 #ECF6FF + 边框 #96CDFF
  &.can_join {
    background: #ecf6ff;
    border: 1px solid #96cdff;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px 12px;
  }

  // 无法回访：浅红底 #FAF2F2 + 边框 #FFD2D2
  &.cannot_revisit {
    background: #faf2f2;
    border: 1px solid #ffd2d2;
  }
}

// 无法回访的子区域
.sub_section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// 子标题：14px 500 #FF5A5A
.sub_title {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #ff5a5a;
  font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, sans-serif;
}

.tag_row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
}

// 患者标签：白底圆角 pill
.patient_tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  background: #fff;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #333;
  font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, sans-serif;
  white-space: nowrap;

  .tag_close {
    font-size: 12px;
    color: #333;
    cursor: pointer;
    &:hover {
      color: #ff4d4f;
    }
  }
}

// 提示信息：橙底 #FFF7EE
.info_tip {
  margin-top: 20px;
}

// 分配健管
.manager_block {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.manager_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .manager_title {
    font-size: 15px;
    font-weight: 500;
    line-height: 21px;
    color: #1a1a1a;
    font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, sans-serif;
    .required {
      color: #ff4d4f;
      margin-left: 4px;
    }
  }
  .warn {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #fc8a0f;
  }
}

// Footer
.confirm_footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 9px;
  padding-top: 20px;
}
</style>
